// Deployment marker: 2025-08-12 v8
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { FileText, PenTool, CheckCircle } from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://admin-api.joulescrm.com';

export function SignContractPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [step, setStep] = useState('loading');
  const [contract, setContract] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [signature, setSignature] = useState('');
  const [isSigning, setIsSigning] = useState(false);

  console.log('SignContractPage loaded, version: 2025-08-12 v7, token:', token);

  useEffect(() => {
    async function fetchContract() {
      if (!token) {
        setError('No token provided');
        setStep('error');
        return;
      }
      console.log('Fetching contract with token:', token);
      const { data, error } = await supabase
        .from('contracts')
        .select('id, signed_document_url, template_id, form_data, project_id, customer_email, customer_name, type, signer1_email, signer1_name, signer1_signature, signer2_email, signer2_name, signer2_signature, signer3_email, signer3_name, signer3_signature, current_signer, signature_token, tenant_id')
        .eq('signature_token', token)
        .single();

      if (error || !data) {
        console.error('Error fetching contract:', error);
        setError('Invalid or expired link');
        setStep('error');
      } else {
        console.log('Contract fetched successfully:', data);
        setContract(data);
        setStep('welcome');
      }
    }
    fetchContract();
  }, [token]);

  // Helper function to get the display name based on contract type
  const getContractTypeName = (type: string | null) => {
    if (!type) return 'Contract';
    switch (type) {
      case 'LOI':
        return 'Letter of Intent';
      case 'TurnKey':
        return 'Solar Installation Agreement';
      case 'MNDA':
        return 'Mutual Non-Disclosure Agreement';
      default:
        return 'Contract';
    }
  };

  // Determine the current signer's name and email
  const getCurrentSigner = () => {
    if (!contract) return { name: '', email: '' };
    console.log('Determining current signer for contract type:', contract.type, 'current_signer:', contract.current_signer);
    switch (contract.current_signer) {
      case 'signer1':
        return { name: contract.signer1_name || '', email: contract.signer1_email || '' };
      case 'signer2':
        return { name: contract.signer2_name || '', email: contract.signer2_email || '' };
      case 'signer3':
        return { name: contract.signer3_name || '', email: contract.signer3_email || '' };
      default:
        return { name: contract.customer_name || '', email: contract.customer_email || '' };
    }
  };

  const handleProceed = () => {
    console.log('Proceeding to signing step');
    setStep('signing');
  };

  const handleSign = async () => {
    if (!signature.trim()) {
      alert('Please enter your full name');
      return;
    }
    if (isSigning) return;
    setIsSigning(true);
    try {
      console.log('Starting signing process for signer:', contract.current_signer, 'with signature:', signature, 'contract type:', contract.type, 'contract id:', contract.id);
      const formData = contract.form_data || {};
      const signatureField = ['LOI', 'MNDA', 'TurnKey', 'Solar Installation Agreement'].includes(contract.type) ? 'Customer Signature' : `signature ${contract.current_signer === 'signer1' ? 1 : contract.current_signer === 'signer2' ? 2 : 3}`;

      // Format today's date for signature date field
      const today = new Date();
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const signatureDate = `${monthNames[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;

      const updatedData = {
        ...formData,
        [signatureField]: signature,
        'Customer Signature Date': signatureDate,
        'Signature Date': signatureDate, // Alternative placeholder name
      };
      console.log('Updated form_data to send to edge function:', updatedData);
      console.log('Calling edge function to generate contract with payload:', {
        action: 'generateContract',
        templateId: contract.template_id,
        projectId: contract.project_id,
        contractId: contract.id,
        data: updatedData,
        signing: true
      });
      const functionsBaseUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || 'https://cqynudrcrwlodsxuowbm.supabase.co/functions/v1';
      const response = await fetch(`${functionsBaseUrl}/contract-builder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'generateContract',
          templateId: contract.template_id,
          projectId: contract.project_id,
          data: updatedData,
          contractId: contract.id,
          signing: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Edge function error:', errorData);
        throw new Error(errorData.message || 'Failed to generate contract');
      }
      const { pdfUrl } = await response.json();
      console.log('Generated PDF URL:', pdfUrl);
      setPdfUrl(pdfUrl);

      // Call the appropriate RPC based on contract type
      const rpcFunction = ['LOI', 'MNDA', 'TurnKey'].includes(contract.type) ? 'sign_contract' : 'sign_contract_new';
      console.log(`Calling RPC function: ${rpcFunction} with token: ${token}, signature: ${signature}, pdfUrl: ${pdfUrl}`);
      const { error: rpcError } = await supabase.rpc(rpcFunction, {
        p_token: token,
        p_signature: signature,
        p_pdf_url: pdfUrl,
      });

      if (rpcError) {
        console.error('RPC error:', rpcFunction, rpcError);
        throw new Error(`Failed to sign contract: ${rpcError.message}`);
      }
      console.log('Contract signed successfully via RPC:', rpcFunction);

      // Log 'signed' event (fire-and-forget)
      const currentSigner = getCurrentSigner();
      supabase
        .from('contract_events')
        .insert({
          contract_id: contract.id,
          event_type: 'signed',
          event_data: {
            signer_name: currentSigner.name || signature,
            signer_email: currentSigner.email,
            contract_type: contract.type,
          },
          actor_type: 'customer',
          actor_name: currentSigner.name || signature,
          tenant_id: contract.tenant_id,
        })
        .then(({ error }) => {
          if (error) console.error('Failed to log signed event:', error);
        });

      // Fetch the updated signature_token for the next signer (if any)
      let newSignatureToken: string | null = null;
      if (contract.current_signer && !['LOI', 'MNDA', 'TurnKey'].includes(contract.type)) {
        const { data: updatedContract, error: fetchError } = await supabase
          .from('contracts')
          .select('signature_token')
          .eq('id', contract.id)
          .single();
        if (fetchError) {
          console.error('Error fetching updated signature_token:', fetchError);
          // Log the error but don't fail the signing process, as signing succeeded
        } else {
          newSignatureToken = updatedContract?.signature_token || null;
          console.log('Fetched new signature_token:', newSignatureToken);
        }
      }

      // Send email to the next signer if applicable (for non-LOI/MNDA/TurnKey contracts)
      if (contract.current_signer && !['LOI', 'MNDA', 'TurnKey'].includes(contract.type)) {
        let nextSigner: string | null = null;
        let nextSignerEmail: string | null = null;
        if (contract.current_signer === 'signer1' && contract.signer2_email) {
          nextSigner = 'signer2';
          nextSignerEmail = contract.signer2_email;
        } else if (contract.current_signer === 'signer2' && contract.signer3_email) {
          nextSigner = 'signer3';
          nextSignerEmail = contract.signer3_email;
        }
        if (nextSigner && nextSignerEmail && newSignatureToken) {
          const signatureLink = `${import.meta.env.VITE_FRONTEND_URL || 'https://go.solerro.com'}/sign?token=${newSignatureToken}`;
          console.log(`Sending signature link to ${nextSigner}:`, nextSignerEmail, 'with link:', signatureLink);
          const emailResponse = await fetch(`${BACKEND_URL}/api/v1/contracts/public/send-contract-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'sendContractEmail',
              customer_email: nextSignerEmail,
              signature_link: signatureLink,
              contract_id: contract.id,
              tenant_id: contract.tenant_id,
            }),
          });
          if (!emailResponse.ok) {
            const errorData = await emailResponse.json();
            console.error('Failed to send signature link email:', errorData);
            // Log the error but don't fail the signing process
            console.warn('Proceeding despite email failure for next signer');
          } else {
            console.log('Signature link email sent successfully to:', nextSignerEmail);
          }
        } else if (nextSigner && nextSignerEmail && !newSignatureToken) {
          console.warn('No signature token available for next signer; skipping email to:', nextSignerEmail);
        }
      }

      // Send download link email to the current signer
      console.log('Sending download link email to:', currentSigner.email, 'with pdfUrl:', pdfUrl);
      try {
        const emailResponse = await fetch(`${BACKEND_URL}/api/v1/contracts/public/send-contract-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'sendDownloadLink',
            customer_email: currentSigner.email,
            download_link: pdfUrl,
            contract_id: contract.id,
            tenant_id: contract.tenant_id,
          }),
        });
        if (!emailResponse.ok) {
          const errorData = await emailResponse.json();
          console.error('Failed to send download link email:', errorData);
          // Log the error but don't fail the signing process
          console.warn('Proceeding despite download link email failure');
        } else {
          console.log('Download link email sent successfully to:', currentSigner.email);
        }
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
        console.warn('Proceeding despite download link email failure');
      }

      setStep('success');
    } catch (error: any) {
      console.error('Error during signing process:', error);
      setError(`Error signing contract: ${error.message || 'Unknown error'}`);
      setStep('error');
    } finally {
      setIsSigning(false);
    }
  };

  // Memoize the PDF URL to prevent iframe refreshes
  const pdfUrlWithTimestamp = useMemo(() => {
    if (contract?.signed_document_url) {
      const timestamp = new Date().getTime();
      return `${contract.signed_document_url}?t=${timestamp}`;
    }
    return '';
  }, [contract]);

  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#BEA04D]"></div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <p className="text-red-600 text-2xl font-semibold tracking-wide animate-pulse">{error}</p>
      </div>
    );
  }

  if (step === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#E0E0E0] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-2xl w-full transform transition-all hover:scale-105 duration-300">
          <div className="flex justify-center mb-6">
            <div className="bg-[#BEA04D] text-white rounded-full p-3 animate-pulse">
              <FileText className="h-8 w-8" />
            </div>
          </div>
          <h2 className="text-4xl font-extrabold text-center mb-4" style={{ color: '#BEA04D' }}>
            Sign Your {getContractTypeName(contract.type)}
          </h2>
          <p className="text-lg text-gray-600 text-center mb-8">
            You’ve been invited by Solerro Solar to review and sign your {getContractTypeName(contract.type)}. Please proceed carefully.
          </p>
          <button
            onClick={handleProceed}
            className="w-full bg-gradient-to-r from-[#BEA04D] to-[#A88C3F] text-white py-4 px-8 rounded-xl font-semibold text-lg shadow-lg hover:from-[#A88C3F] hover:to-[#BEA04D] focus:ring-4 focus:ring-[#BEA04D] transition duration-300 transform hover:-translate-y-1"
          >
            Proceed to Signing
          </button>
        </div>
      </div>
    );
  }

  if (step === 'signing') {
    const currentSigner = getCurrentSigner();
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#E0E0E0] p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-5xl w-full border border-gray-200 grid gap-8">
          <h2 className="text-3xl font-bold text-center flex items-center justify-center" style={{ color: '#BEA04D' }}>
            <FileText className="h-8 w-8 mr-3" /> Sign Your {getContractTypeName(contract.type)}
          </h2>
          <div className="bg-gray-50 p-6 rounded-xl shadow-inner border border-gray-100">
            <iframe src={pdfUrlWithTimestamp} className="w-full h-[60vh] sm:h-[70vh] md:h-[80vh] rounded-lg" title="Contract PDF" />
          </div>
          <div className="space-y-6">
            <p className="text-sm text-gray-500 italic bg-gray-50 p-4 rounded-lg shadow-sm">
              By signing, you agree to the legally binding terms and conditions outlined above.
            </p>
            <div className="relative">
              <label htmlFor="signature" className="flex items-center text-lg font-medium text-gray-700 mb-2">
                <PenTool className="h-6 w-6 mr-2 text-[#BEA04D]" /> Please type your full name to sign ({currentSigner.name})
              </label>
              <input
                id="signature"
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                className="w-full p-4 border-b-2 border-gray-300 bg-gray-50 rounded-t-lg shadow-sm focus:border-[#BEA04D] focus:ring-0 transition-all duration-200 text-lg placeholder-gray-400"
                placeholder="Type your full name here"
              />
            </div>
            <button
              onClick={handleSign}
              disabled={isSigning}
              className={`w-full bg-gradient-to-r from-[#BEA04D] to-[#A88C3F] text-white py-4 rounded-xl font-semibold text-lg shadow-lg transition duration-300 transform hover:-translate-y-1 ${isSigning ? 'opacity-50 cursor-not-allowed' : 'hover:from-[#A88C3F] hover:to-[#BEA04D]'}`}
            >
              {isSigning ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                  Signing...
                </div>
              ) : (
                'Sign Contract'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    const handleDownload = () => {
      const a = document.createElement('a');
      a.href = pdfUrl || '';
      a.download = 'signed_contract.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-10 max-w-2xl w-full transform transition-all hover:scale-105 duration-300">
          <div className="flex justify-center mb-6">
            <CheckCircle className="h-12 w-12 text-green-600 animate-bounce" />
          </div>
          <p className="text-2xl font-semibold text-green-700 text-center mb-4">
            {getContractTypeName(contract.type)} Signed Successfully!
          </p>
          <p className="text-gray-600 text-center mb-8">
            Your agreement has been recorded. Download your copy below.
          </p>
          <button
            onClick={handleDownload}
            className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-4 rounded-xl font-semibold text-lg shadow-lg hover:from-green-700 hover:to-teal-700 focus:ring-4 focus:ring-green-300 transition duration-300 transform hover:-translate-y-1"
          >
            Download Signed Contract
          </button>
        </div>
      </div>
    );
  }

  return null;
}